import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetVendorQuery } from "@generated";
import { VendorForm } from "../VendorForm/VendorForm";
import { useVendorMutations } from "../api";
import { emptyVendorForm } from "../types";
import type { VendorFormValues } from "../types";

const FORM_ID = "vendor-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useVendorMutations();
  // refetchOnMountOrArgChange so re-entering Edit (or arriving from a list
  // changed elsewhere) re-requests the record rather than trusting the cache.
  const { data, isLoading, refetch } = useV2GetVendorQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<VendorFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<VendorFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyVendorForm;
    return {
      name: attrs.name ?? "",
      streetAddress: attrs["street-address"] ?? "",
      street2: attrs.street2 ?? "",
      city: attrs.city ?? "",
      state: attrs.state ?? "",
      postalCode: attrs["postal-code"] ?? "",
      country: attrs.country ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: VendorFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update vendor", err);
      setToast(saveErrorToast(err, "vendor"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Vendor"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <VendorForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
